import { memo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Language, LanguageContainer, LanguagesContainer, LanguagesSelector, SelectedLanguage } from './LanguagePicker.styled';
import { useI18n } from '@i18n/useI18n';
import { LangMap } from '@i18n/languages';
import { ensureLanguageLoaded } from '@i18n/lazyLanguages';
import routeChangeHook from '@hooks/routeChangeHook';
import { useStore } from '@store/appStore';
import db from '@utils/db';
import ChevronDown from '@assets/svg/chevron-down.svg';

// flags
import Danish_flag from '@assets/svg/language/danish.svg';
import English_flag from '@assets/svg/language/english.svg';
import German_flag from '@assets/svg/language/german.svg';
import Netherlands_flag from '@assets/svg/language/netherlands.svg';
import Norway_flag from '@assets/svg/language/norway.svg';
import Swedish_flag from '@assets/svg/language/sweden.svg';
import France_flag from '@assets/svg/language/france.svg';
import Finnish_flag from '@assets/svg/language/finland.svg';
import Italian_flag from '@assets/svg/language/italy.svg';
import Brazilian_flag from '@assets/svg/language/brazil.svg';
import Turkisk_flag from '@assets/svg/language/turkey.svg';
import Icelandic_flag from '@assets/svg/language/iceland.svg';
import { useDetectClickOutside } from 'react-detect-click-outside';

// spain.svg traces the full coat of arms at high fidelity (1000+ paths) and
// is ~225KB on its own, vs well under 5KB for every other flag here - loading
// it on demand keeps that weight out of _app for every visitor.
const Spanish_flag = dynamic(() => import('@assets/svg/language/spain.svg'), { ssr: false });

interface IProps {
  top?: string;
  right?: string;
  background?: string;
}

export const LanguagePicker = (props: IProps) => {
  const [langOpen, setLangOpen] = useState(false);
  const i18n = useI18n();
  const { client, systemSettings, settings } = useStore();
  routeChangeHook(() => setLangOpen(false));
  const ClickOutside = () => {
    setLangOpen(false);
  };
  const ref = useDetectClickOutside({ onTriggered: ClickOutside });

  const flags = {
    da: <Danish_flag />,
    en: <English_flag />,
    de: <German_flag />,
    nl: <Netherlands_flag />,
    fi: <Finnish_flag />,
    se: <Swedish_flag />,
    no: <Norway_flag />,
    fr: <France_flag />,
    es: <Spanish_flag />,
    it: <Italian_flag />,
    pt: <Brazilian_flag />,
    tr: <Turkisk_flag />,
    is: <Icelandic_flag />
  };


  let availableLanguages = [];
  let l = systemSettings?.find((s) => s.APP_Setting_ID === 18);
  if (l && l.value) availableLanguages = l.value.split(';');
  let lngs = Array.from(LangMap).filter((l) => availableLanguages.includes(l[0]));
  if (settings?.DEMO === true || settings?.DEMO === 'true') lngs = Array.from(LangMap);

  return (
    <LanguageContainer
      top={props.top ? props.top : null}
      right={props.right ? props.right : null}
      background={props.background ? props.background : null}
      ref={ref}
      open={langOpen}
      onClick={() => setLangOpen(!langOpen)}
    >
      <SelectedLanguage>
        {flags[i18n.language]}
        <ChevronDown />
      </SelectedLanguage>
      <LanguagesContainer open={langOpen}>
        <LanguagesSelector>
          {lngs.map(([langCode, lang]) => (
            <Language
              onClick={async () => {
                setLangOpen(false);
                if (client && client.UserName) db.setTeamsBotLanguage(client.UserName, langCode);
                db.setUserSetting(client.UserName + '@' + client.Domain, 16, langCode, false);
                await ensureLanguageLoaded(i18n, langCode);
                i18n.changeLanguage(langCode);
              }}
              current={langCode === i18n.language}
              disabled={langCode === i18n.language}
              key={langCode}
              lang={langCode}
            >
              {flags[langCode]}
              <span>{lang.label}</span>
            </Language>
          ))}
        </LanguagesSelector>
      </LanguagesContainer>
    </LanguageContainer>
  );
};

export default memo(LanguagePicker);
